import { useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw, RotateCcw, Save, Image as ImageIcon } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import MediaPicker from "@/components/admin/MediaPicker";
import { supabase } from "@/integrations/supabase/client";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { PAGE_CONTENT_SCHEMA, isValidUrlOrPath } from "@/lib/pageContentSchema";

interface BlockRow {
  id: string;
  page_key: string;
  section_key: string;
  field_key: string;
  field_type: string;
  field_value: string | null;
  is_active: boolean;
  language: string;
}

const LANGUAGE = "en";

const AdminPages = () => {
  const { user } = useAdminAuth();
  const [pageKey, setPageKey] = useState<string>(PAGE_CONTENT_SCHEMA[0].page_key);
  const [rows, setRows] = useState<BlockRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [draftValues, setDraftValues] = useState<Record<string, string>>({});
  const [mediaPickerFor, setMediaPickerFor] = useState<string | null>(null);

  const page = useMemo(
    () => PAGE_CONTENT_SCHEMA.find((p) => p.page_key === pageKey)!,
    [pageKey],
  );

  useEffect(() => {
    document.title = "Pages | Hadassah Homes Admin";
  }, []);

  useEffect(() => {
    void load(pageKey);
  }, [pageKey]);

  const load = async (pk: string) => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from("page_content_blocks")
      .select("id, page_key, section_key, field_key, field_type, field_value, is_active, language")
      .eq("page_key", pk)
      .eq("language", LANGUAGE);
    if (err) {
      setError(err.message);
      setRows([]);
    } else {
      setRows((data ?? []) as unknown as BlockRow[]);
    }
    setDraftValues({});
    setLoading(false);
  };

  const rowFor = (sectionKey: string, fieldKey: string) =>
    rows.find((r) => r.section_key === sectionKey && r.field_key === fieldKey);

  const draftKey = (sectionKey: string, fieldKey: string) => `${sectionKey}.${fieldKey}`;

  const currentValue = (sectionKey: string, fieldKey: string): string => {
    const k = draftKey(sectionKey, fieldKey);
    if (k in draftValues) return draftValues[k];
    return rowFor(sectionKey, fieldKey)?.field_value ?? "";
  };

  const setDraft = (sectionKey: string, fieldKey: string, value: string) => {
    setDraftValues((prev) => ({ ...prev, [draftKey(sectionKey, fieldKey)]: value }));
  };

  const handleSave = async (sectionKey: string, fieldKey: string, fieldType: string) => {
    const value = currentValue(sectionKey, fieldKey).trim();
    const k = draftKey(sectionKey, fieldKey);
    if ((fieldType === "url" || fieldType === "image_url") && value && !isValidUrlOrPath(value)) {
      toast.error("Must be an internal path (/...) or a full URL");
      return;
    }
    setSavingKey(k);
    const existing = rowFor(sectionKey, fieldKey);
    if (existing) {
      const { data, error: err } = await supabase
        .from("page_content_blocks")
        .update({
          field_value: value || null,
          field_type: fieldType,
          is_active: true,
          updated_by: user?.id ?? null,
        } as never)
        .eq("id", existing.id)
        .select()
        .single();
      setSavingKey(null);
      if (err) return toast.error(err.message);
      setRows((prev) => prev.map((r) => (r.id === existing.id ? (data as unknown as BlockRow) : r)));
      setDraftValues((prev) => {
        const { [k]: _omit, ...rest } = prev;
        return rest;
      });
      toast.success("Saved");
    } else {
      if (!value) {
        setSavingKey(null);
        toast.error("Enter a value before saving the first override");
        return;
      }
      const { data, error: err } = await supabase
        .from("page_content_blocks")
        .insert({
          page_key: pageKey,
          section_key: sectionKey,
          field_key: fieldKey,
          field_type: fieldType,
          field_value: value,
          language: LANGUAGE,
          is_active: true,
          created_by: user?.id ?? null,
          updated_by: user?.id ?? null,
        } as never)
        .select()
        .single();
      setSavingKey(null);
      if (err) return toast.error(err.message);
      setRows((prev) => [...prev, data as unknown as BlockRow]);
      setDraftValues((prev) => {
        const { [k]: _omit, ...rest } = prev;
        return rest;
      });
      toast.success("Override created");
    }
  };

  const handleResetToFallback = async (sectionKey: string, fieldKey: string) => {
    const existing = rowFor(sectionKey, fieldKey);
    if (!existing) {
      // Just clear local draft
      setDraftValues((prev) => {
        const { [draftKey(sectionKey, fieldKey)]: _omit, ...rest } = prev;
        return rest;
      });
      return;
    }
    const k = draftKey(sectionKey, fieldKey);
    setSavingKey(k);
    const { data, error: err } = await supabase
      .from("page_content_blocks")
      .update({ is_active: false, updated_by: user?.id ?? null } as never)
      .eq("id", existing.id)
      .select()
      .single();
    setSavingKey(null);
    if (err) return toast.error(err.message);
    setRows((prev) => prev.map((r) => (r.id === existing.id ? (data as unknown as BlockRow) : r)));
    setDraftValues((prev) => {
      const { [k]: _omit, ...rest } = prev;
      return rest;
    });
    toast.success("Reverted to fallback (override deactivated)");
  };

  return (
    <AdminLayout
      title="Pages"
      description="Edit selected text fields on key pages. Layout and design remain controlled by the website. Reset to fallback deactivates the override — it does not delete it."
      publicHref="/"
      publicLabel="View website"
    >
      <div className="mx-auto max-w-4xl space-y-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm text-muted-foreground">
              Override selected text, link, and image fields on key pages. Each field has two states:{" "}
              <span className="font-semibold text-foreground">Override active</span> (your value is shown publicly) or{" "}
              <span className="font-semibold text-foreground">Using fallback</span> (the built-in copy is shown).
            </p>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Saving an empty value still stores an override but the public site falls back to built-in copy. Use <span className="font-semibold">Reset to fallback</span> to deactivate an override without deleting it.
            </p>
          </div>
          <div className="flex gap-2">
            <select
              value={pageKey}
              onChange={(e) => setPageKey(e.target.value)}
              className="h-10 rounded-none border border-input bg-background px-3 text-sm"
            >
              {PAGE_CONTENT_SCHEMA.map((p) => (
                <option key={p.page_key} value={p.page_key}>
                  {p.label}
                </option>
              ))}
            </select>
            <Button
              variant="outline"
              onClick={() => void load(pageKey)}
              disabled={loading}
              className="rounded-none"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16 border border-border bg-card">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="border border-destructive/40 bg-destructive/5 p-6 text-destructive">{error}</div>
        ) : (
          <div className="space-y-6">
            {page.sections.map((section) => (
              <section key={section.section_key} className="border border-border bg-card">
                <header className="border-b border-border bg-muted/40 px-5 py-3">
                  <h2 className="font-headline text-lg text-foreground">{section.label}</h2>
                  <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                    {pageKey}.{section.section_key}
                  </p>
                </header>
                <div className="divide-y divide-border">
                  {section.fields.map((field) => {
                    const k = draftKey(section.section_key, field.field_key);
                    const row = rowFor(section.section_key, field.field_key);
                    const overrideActive = !!row && row.is_active;
                    const dirty = k in draftValues;
                    const value = currentValue(section.section_key, field.field_key);
                    const saving = savingKey === k;
                    return (
                      <div key={field.field_key} className="grid grid-cols-1 gap-3 p-5 md:grid-cols-[220px_1fr]">
                        <div>
                          <p className="text-sm font-medium text-foreground">{field.label}</p>
                          <p className="text-[11px] text-muted-foreground">
                            {section.section_key}.{field.field_key}
                          </p>
                          {field.helper ? (
                            <p className="mt-1 text-[11px] text-muted-foreground">{field.helper}</p>
                          ) : null}
                          <div className="mt-2">
                            <span
                              className={`inline-flex items-center border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${
                                overrideActive
                                  ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700"
                                  : row
                                    ? "border-border bg-muted text-muted-foreground"
                                    : "border-border bg-background text-muted-foreground"
                              }`}
                            >
                              {overrideActive ? "Override active" : row ? "Inactive (fallback)" : "Using fallback"}
                            </span>
                          </div>
                        </div>
                        <div className="space-y-2">
                          {field.type === "textarea" ? (
                            <Textarea
                              rows={4}
                              value={value}
                              onChange={(e) => setDraft(section.section_key, field.field_key, e.target.value)}
                              placeholder={field.placeholder ?? "Leave empty to use fallback"}
                              className="rounded-none"
                            />
                          ) : field.type === "image_url" ? (
                            <div className="flex items-center gap-2">
                              <Input
                                value={value}
                                onChange={(e) => setDraft(section.section_key, field.field_key, e.target.value)}
                                placeholder="Image URL"
                                className="rounded-none"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => setMediaPickerFor(k)}
                                className="rounded-none"
                              >
                                <ImageIcon className="mr-2 h-4 w-4" />
                                Pick
                              </Button>
                              <MediaPicker
                                open={mediaPickerFor === k}
                                onOpenChange={(o) => setMediaPickerFor(o ? k : null)}
                                onSelect={(asset) => {
                                  setDraft(section.section_key, field.field_key, asset.public_url);
                                  setMediaPickerFor(null);
                                }}
                              />
                            </div>
                          ) : (
                            <Input
                              value={value}
                              onChange={(e) => setDraft(section.section_key, field.field_key, e.target.value)}
                              placeholder={field.placeholder ?? "Leave empty to use fallback"}
                              className="rounded-none"
                              type={field.type === "url" ? "text" : "text"}
                            />
                          )}
                          <div className="flex flex-wrap items-center gap-2">
                            <Button
                              size="sm"
                              variant="gold"
                              disabled={saving || (!dirty && overrideActive)}
                              onClick={() =>
                                void handleSave(section.section_key, field.field_key, field.type === "image_url" ? "image_url" : field.type === "url" ? "link_url" : field.type === "textarea" ? "textarea" : "text")
                              }
                              className="rounded-none"
                            >
                              {saving ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <Save className="mr-2 h-4 w-4" />
                              )}
                              {row ? "Save override" : "Create override"}
                            </Button>
                            {row ? (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={saving}
                                onClick={() => void handleResetToFallback(section.section_key, field.field_key)}
                                className="rounded-none"
                                title="Deactivates this override; site shows built-in fallback. Not deleted."
                              >
                                <RotateCcw className="mr-2 h-4 w-4" />
                                Reset to fallback
                              </Button>
                            ) : null}
                            {dirty ? (
                              <span className="text-[11px] text-muted-foreground">Unsaved changes</span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
            <p className="text-[11px] text-muted-foreground">
              Tip: Leaving a field empty and saving will store an empty override; the public site will fall back to built-in copy. Use "Reset to fallback" to fully deactivate an override without deleting history.
            </p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminPages;
